import { Component, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { AMMUNITION_STOCK_TYPE, AmmunitionStock, BusinessUnit, COST_CENTRE_TYPE_WILDLIFE, getDefaultBusinessUnitFromUser, getDefaultCostCentreFromUser } from 'assets-model';
import { AmmunitionStockService, BusinessUnitService, CostCentreService } from 'assets-service';
import { AmmunitionStockSearchComponentBase } from './ammunition-stock-search.component.base';
import { MatSelect } from '@angular/material/select';


@Component({
  selector: 'app-ammunition-stock-search',
  templateUrl: './ammunition-stock-search.component.html',
  styleUrls: ['./ammunition-stock-search.component.scss']
})
export class AmmunitionStockSearchComponent extends AmmunitionStockSearchComponentBase {

  displayedColumns: string[] = ['businessUnit.buId', 'costCentre.ccId', 'calibre', 'make', 'weight', 'currentStockAmount', 'closed', 'createdBy', 'createdDate', 'actions'];

  businessUnitIdQueryParam;
  costCentreIdQueryParam;

  private readyForSearch: boolean = false;

  hasPendingIssueAcknowledgementsFilter: boolean;
  @ViewChild('hasPendingIssueAcknowledgementsFilterInput') hasPendingIssueAcknowledgementsFilterInput: MatSelect;

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected ammunitionStockService: AmmunitionStockService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(route, router, userStorage, dialog, ammunitionStockService, businessUnitService, costCentreService, fb, formStorage, injector);
    this.route.queryParams.subscribe(params => {
      this.businessUnitIdQueryParam = params['businessUnitId'];
      this.costCentreIdQueryParam = params['costCentreId'];
    });
    this.closedFilter = false;
    this.searchForm.addControl('hasPendingIssueAcknowledgements', new FormControl(null));
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedSelectEventListener(this.hasPendingIssueAcknowledgementsFilterInput);
  }

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.businessUnitLoadSelectionList();
    this.costCentreLoadSelectionList();

    if (!this.restoreFormFromParams(this.searchForm)) {
      this.resetSearchForm();
      this.restoreFormChanges();
    }
    this.userStorage.nextNonNullItem().subscribe(u => this.setDefaultLocationFromUser());
  }

  createServiceArguments() {
    var args = super.createServiceArguments();
    args['hasPendingIssueAcknowledgements'] = this.hasPendingIssueAcknowledgementsFilter;
    return args;
  }

  clearFilters(): void {
    super.clearFilters();
    this.hasPendingIssueAcknowledgementsFilter = null;
  }

  resetSearchForm() {
  }

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    super.businessUnitChanged(value, unsetDependents);
    this.costCentreLoadSelectionList();
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findActive(true).subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
        if (this.businessUnitIdQueryParam) {
          this.businessUnitFilter = this.businessUnitSelectionList.find(bu => bu.buId == this.businessUnitIdQueryParam);
          this.businessUnitIdQueryParam = null;
        } else { //if no business unit param, we can search, otherwise wait for cost centre load
          this.readyForSearch = true;
          this.forceSearch();
        }
      }
    );
  }

  costCentreLoadSelectionList() {
    if (this.businessUnitFilter != null) {
      this.costCentreService.findByBusinessUnitForType(this.businessUnitFilter?.buId, COST_CENTRE_TYPE_WILDLIFE).subscribe(
        data => {
          this.costCentreSelectionList = data.items;
          this.costCentreFilter = null;
          this.costCentre.enable();
          if (this.costCentreIdQueryParam) {
            this.costCentreFilter = this.costCentreSelectionList.find(cc => cc.ccId == this.costCentreIdQueryParam);
            this.costCentreIdQueryParam = null;
          }
          //now we can search
          this.readyForSearch = true;
          this.forceSearch();
        }
      );
    } else {
      this.costCentreSelectionList = [];
      this.costCentreFilter = null;
      this.costCentre.disable();
    }
  }

  currentStockAmountDisplay(stock: AmmunitionStock) {
    if (stock.hasStocktake && stock.currentStockAmount != null) {
      return stock.currentStockAmount;
    } else {
      return 'No Stocktake';
    }
  }

  protected transformParamsToModelSetters(params: Params): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    var setter = this.transformAllPropertiesToModelSetters();
    setter['hasPendingIssueAcknowledgements'] = (value: any, formControl: FormControl, formControlName: string) => {
      this.hasPendingIssueAcknowledgementsFilter = value == 'true' ? true : value == 'false' ? false : null;
    }
    return setter;
  }

  isReadyForSearch(): boolean {
    return this.readyForSearch;
  }

  allowsAmmunitionStockActions() {
    return true;
  }

  allowsStocktake() {
    return this.allowsAmmunitionStockActions();
  }

  allowsAdditions() {
    return this.allowsAmmunitionStockActions();
  }

  allowsIssue() {
    return this.allowsAmmunitionStockActions();
  }

  allowsReturn() {
    return this.allowsAmmunitionStockActions();
  }

  stocktakeRoute() {
    return [...AMMUNITION_STOCK_TYPE.stocktakeRouteBase]
  }

  additionsRoute() {
    return [...AMMUNITION_STOCK_TYPE.additionsRouteBase]
  }

  issueRoute() {
    return [...AMMUNITION_STOCK_TYPE.issueRouteBase]
  }

  returnRoute() {
    return [...AMMUNITION_STOCK_TYPE.returnRouteBase]
  }


  stocktakeRouteQueryParams() {
    var params = super.currentRouteBackRouteQueryParams();
    params['businessUnitId'] = this.businessUnitFilter?.buId;
    params['costCentreId'] = this.costCentreFilter?.ccId;
    return params;
  }

  additionsRouteQueryParams() {
    var params = super.currentRouteBackRouteQueryParams();
    params['businessUnitId'] = this.businessUnitFilter?.buId;
    params['costCentreId'] = this.costCentreFilter?.ccId;
    return params;
  }

  issueRouteQueryParams() {
    var params = super.currentRouteBackRouteQueryParams();
    params['businessUnitId'] = this.businessUnitFilter?.buId;
    params['costCentreId'] = this.costCentreFilter?.ccId;
    return params;
  }

  returnRouteQueryParams() {
    var params = super.currentRouteBackRouteQueryParams();
    params['businessUnitId'] = this.businessUnitFilter?.buId;
    params['costCentreId'] = this.costCentreFilter?.ccId;
    return params;
  }
  setDefaultLocationFromUser() {
    this.setDefaultBusinessUnitFromUser();
    this.setDefaultCostCentreFromUser();
  }
  setDefaultBusinessUnitFromUser() {
    if (this.businessUnitFilter != null || this.eAssetsUser == null) {
      return;
    }
    this.businessUnitFilter = getDefaultBusinessUnitFromUser(this.eAssetsUser);
  }
  setDefaultCostCentreFromUser() {
    if (this.costCentreFilter != null || this.eAssetsUser == null) {
      return;
    }
    this.costCentreFilter = getDefaultCostCentreFromUser(this.eAssetsUser);
  }

  allowsDelete(item: AmmunitionStock): boolean {
    return false;
  }

  hasPendingIssueAcknowledgementsChanging(value: boolean, unsetDependents: boolean = true) {}
  
  hasPendingIssueAcknowledgementsChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  get hasPendingIssueAcknowledgements() {
    return this.searchForm && this.searchForm.get('hasPendingIssueAcknowledgements');
  }
}



